import ToolbarService from './ToolbarService';
import { isMobile } from '@vindr/utils/mobile';

const TOOL_TYPES = {
  radioGroup: 'ohif.radioGroup',
  action: 'ohif.action',
  splitButton: 'vindr.splitButton',
};

/**
 * Link {@link: platform/core/src/services/ToolBarService/ToolbarService.ts:80}
 *
 * @param {*} type - 'tool' | 'action' | 'toggle'
 * @param {*} id
 * @param {*} icon
 * @param {*} label
 */
function _createButton(
  type,
  id,
  icon,
  label,
  commands,
  tooltip,
  items = null,
  hidden
) {
  return {
    type,
    id,
    icon,
    label,
    commands,
    tooltip,
    items,
    hidden,
  };
}

function _createCommands(commandName, toolName, toolGroupIds) {
  return toolGroupIds.map(toolGroupId => ({
    /* It's a command that is being run when the button is clicked. */
    commandName,
    commandOptions: {
      toolName,
      toolGroupId,
    },
    context: 'CORNERSTONE',
  }));
}

const _createActionButton = _createButton.bind(null, 'action');
const _createToggleButton = _createButton.bind(null, 'toggle');
const _createToolButton = _createButton.bind(null, 'tool');
const hiddenOnMobile = isMobile();

const evaluate = {
  cornerstoneTool: 'evaluate.cornerstoneTool',
  action: 'evaluate.action',
  sidePanel: 'evaluate.sidePanel',
  vtkjs: 'evaluate.vtkjs',
  cine: 'evaluate.cine',
  syncGroup: 'evaluate.syncGroup',
};

const defaultProtocols = [
  'default',
  'orthogonal',
  'ct',
  'mg2x2',
  'mg1x2lcclmlo',
  'mg1x2rccrmlo',
];

/**
 *
 * @param {*} id - preset number (from above import)
 * @param {*} name
 * @param {*} width window width
 * @param {*} center window center (level)
 */
function _createWwwcPreset(id, name, width, center) {
  return {
    id: id.toString(),
    label: `${id} - ${name} (ww: ${width}, wc: ${center})`,
    type: 'action',
    isAction: true,
    commands: [
      {
        commandName: 'setWindowLevel',
        commandOptions: {
          description: name,
          window: width,
          level: center,
        },
        context: 'CORNERSTONE',
      },
    ],
  };
}

function _createSetToolActiveCommands(
  toolName,
  toolGroupIds = ['default', 'mpr']
) {
  return toolGroupIds.map(toolGroupId => ({
    commandName: 'setToolActive',
    commandOptions: {
      toolGroupId,
      toolName,
    },
    context: 'CORNERSTONE',
  }));
}

export const setToolActiveToolbar = {
  commandName: 'setToolActiveToolbar',
  commandOptions: {
    toolGroupIds: ['default', 'mpr', 'SRToolGroup', 'volume3d'],
  },
};

const toolbarButtons = [
  {
    id: 'ViewMode',
    uiType: 'vindr.selection',
    props: {
      groupId: 'ViewMode',
      isRadio: true, // ?
      protocol: [...defaultProtocols, 'ecg', 'mpr', 'vr'],
      evaluate: 'evaluate.hangingProtocol',
      primary: _createToolButton(
        'default',
        null,
        'Default',
        [
          {
            commandName: 'toggleMPR',
            commandOptions: {
              forceState: false,
            },
            context: 'CORNERSTONE',
          },
        ],
        'Protocol'
      ),
      items: [
        ToolbarService.createButton({
          id: 'default',
          label: 'Default',
          tooltip: 'Protocol',
          commands: [
            {
              commandName: 'toggleDefaultProtocol',
              commandOptions: {
                forceState: true,
              },
              context: 'CORNERSTONE',
            },
          ],
        }),
        ToolbarService.createButton({
          id: 'mpr',
          label: 'MPR',
          tooltip: 'Protocol',
          commands: [
            {
              commandName: 'toggleMPR',
              commandOptions: {
                forceState: true,
              },
              context: 'CORNERSTONE',
            },
          ],
        }),
        ToolbarService.createButton({
          id: 'vr',
          label: 'VR',
          tooltip: 'Protocol',
          commands: [
            {
              commandName: 'toggleVR',
              commandOptions: {
                forceState: true,
              },
              context: 'CORNERSTONE',
            },
          ],
        }),
        ToolbarService.createButton({
          id: 'ct',
          label: 'CT/MR',
          tooltip: 'Protocol',
          commands: [
            {
              commandName: 'toggleCT',
              commandOptions: {
                forceState: true,
              },
              context: 'CORNERSTONE',
            },
          ],
          hidden: hiddenOnMobile,
        }),
        ToolbarService.createButton({
          id: 'mg2x2',
          label: 'MG 2x2',
          tooltip: 'Protocol',
          commands: [
            {
              commandName: 'toggleMG',
              commandOptions: {
                forceState: true,
              },
              context: 'CORNERSTONE',
            },
          ],
          hidden: hiddenOnMobile,
        }),
        ToolbarService.createButton({
          id: 'mg1x2lcclmlo',
          label: 'MG LCC LMLO',
          tooltip: 'Protocol',
          commands: [
            {
              commandName: 'toggleMG1x2lcclmlo',
              commandOptions: {
                forceState: true,
              },
              context: 'CORNERSTONE',
            },
          ],
          hidden: hiddenOnMobile,
        }),
        ToolbarService.createButton({
          id: 'mg1x2rccrmlo',
          label: 'MG RCC RMLO',
          tooltip: 'Protocol',
          commands: [
            {
              commandName: 'toggleMG1x2rccrmlo',
              commandOptions: {
                forceState: true,
              },
              context: 'CORNERSTONE',
            },
          ],
          hidden: hiddenOnMobile,
        }),
        ToolbarService.createButton({
          id: 'ecg',
          label: 'ECG',
          tooltip: 'Protocol',
          commands: [
            {
              commandName: 'toggleECG',
              commandOptions: {
                forceState: true,
              },
              context: 'CORNERSTONE',
            },
          ],
        }),
      ],
    },
  },
  {
    id: 'Synchronization',
    uiType: 'vindr.checkboxGroup',
    props: {
      groupId: 'Synchronization',
      protocol: [...defaultProtocols, 'mpr'],
      label: 'Series Synchronization',
      tooltip: 'Series Synchronization',
      icon: 'link',
      // Switch?
      items: [
        ToolbarService.createButton({
          id: 'ReferenceLines',
          label: 'Reference Lines',
          tooltip: 'Reference Lines',
          commands: [
            {
              commandName: 'toggleReferenceLines',
              context: 'CORNERSTONE',
            },
          ],
          evaluate: 'evaluate.cornerstoneTool.toggle',
          protocol: [...defaultProtocols],
        }),
        ToolbarService.createButton({
          id: 'StackImageSync',
          label: 'Synchronize Slice Position',
          tooltip: 'Stack Image Sync',
          commands: [
            {
              commandName: 'toggleStackImageSync',
              context: 'CORNERSTONE',
            },
          ],
          evaluate: evaluate.syncGroup,
          protocol: [...defaultProtocols],
        }),
        ToolbarService.createButton({
          id: 'ZoomSync',
          label: 'Synchronize Zoom',
          tooltip: 'Synchronize Zoom',
          commands: [
            {
              commandName: 'toggleZoomSync',
              context: 'CORNERSTONE',
            },
          ],
          evaluate: evaluate.syncGroup,
        }),
        ToolbarService.createButton({
          id: 'PanSync',
          label: 'Synchronize Pan',
          tooltip: 'Synchronize Pan',
          commands: [
            {
              commandName: 'togglePanSync',
              context: 'CORNERSTONE',
            },
          ],
          evaluate: evaluate.syncGroup,
        }),
        ToolbarService.createButton({
          id: 'VoiSync',
          label: 'Synchronize Window Settings',
          tooltip: 'Synchronize Window Settings',
          commands: [
            {
              commandName: 'toggleVoiSync',
              context: 'CORNERSTONE',
            },
          ],
          evaluate: evaluate.syncGroup,
        }),
      ],
    },
  },
  {
    id: 'StackImageSync',
    uiType: TOOL_TYPES.action,
    props: {
      type: 'toggle',
      icon: 'link',
      label: 'Stack Image Sync',
      protocol: [...defaultProtocols],
      commands: [
        {
          commandName: 'toggleStackImageSync',
          commandOptions: {},
          context: 'CORNERSTONE',
        },
      ],
    },
  },
  {
    id: 'TrackballRotate',
    uiType: TOOL_TYPES.radioGroup,
    protocol: [...defaultProtocols, 'mpr', 'vr'],
    props: {
      type: 'tool',
      icon: 'tool-vr-rotate',
      label: '3D Rotate',
      tooltip: '3D Rotate',
      commands: setToolActiveToolbar,
      evaluate: evaluate.cornerstoneTool,
    },
  },
  {
    id: 'Rotation',
    uiType: TOOL_TYPES.splitButton,
    props: {
      groupId: 'Rotation',
      isRadio: true, // ?
      protocol: [...defaultProtocols],
      // Switch?
      primary: ToolbarService.createButton({
        id: 'RotateRight',
        icon: 'tool-rotate-right',
        label: 'Rotate Right',
        tooltip: 'Rotate Right +90',
        commands: [
          {
            commandName: 'rotateViewportCW',
            context: 'CORNERSTONE',
          },
        ],
        evaluate: evaluate.action,
      }),
      secondary: {
        tooltip: 'More Tools',
      },
      items: [
        ToolbarService.createButton({
          id: 'RotateRight',
          icon: 'tool-rotate-right',
          label: 'Rotate Right',
          tooltip: 'Rotate Right +90',
          commands: [
            {
              commandName: 'rotateViewportCW',
              context: 'CORNERSTONE',
            },
          ],
          evaluate: evaluate.action,
        }),
        ToolbarService.createButton({
          id: 'FlipHorizontal',
          icon: 'tool-flip-horizontal',
          label: 'Flip Horizontally',
          tooltip: 'Flip Horizontally',
          commands: [
            {
              commandName: 'flipViewportHorizontal',
              context: 'CORNERSTONE',
            },
          ],
          evaluate: evaluate.action,
        }),
        ToolbarService.createButton({
          id: 'FlipVertical',
          icon: 'tool-flip-vertical',
          label: 'Flip Vertical',
          tooltip: 'Flip Vertical',
          commands: [
            {
              commandName: 'flipViewportVertical',
              context: 'CORNERSTONE',
            },
          ],
          evaluate: evaluate.action,
        }),
      ],
    },
  },
  {
    id: 'ResetAnnotation',
    uiType: TOOL_TYPES.action,
    props: {
      type: 'action',
      icon: 'tool-reset-annotation',
      label: 'Reset Annotation',
      protocol: [...defaultProtocols, 'mpr', 'ecg'],
      commands: [
        {
          commandName: 'resetAnnotation',
          commandOptions: {},
          context: 'CORNERSTONE',
        },
      ],
    },
  },
  {
    id: 'Reset',
    uiType: TOOL_TYPES.action,
    props: {
      type: 'action',
      icon: 'tool-reset',
      label: 'Reset View',
      protocol: [...defaultProtocols, 'ecg', 'mpr', 'vr'],
      commands: [
        {
          commandName: 'resetViewport',
          commandOptions: {},
          context: 'CORNERSTONE',
        },
      ],
    },
  },
  {
    id: 'StackScroll',
    uiType: TOOL_TYPES.radioGroup,
    props: {
      type: 'tool',
      icon: 'tool-stack-scroll',
      label: 'Stack Scroll',
      protocol: [...defaultProtocols, 'ecg'],
      commands: _createSetToolActiveCommands('StackScroll'),
      evaluate: evaluate.cornerstoneTool,
    },
  },
  {
    id: 'Invert',
    uiType: TOOL_TYPES.action,
    props: {
      type: 'toggle',
      icon: 'tool-invert',
      evaluate: 'evaluate.colorInvert',
      label: 'Invert Colors',
      protocol: [...defaultProtocols],
      commands: [
        {
          commandName: 'invertViewport',
          commandOptions: {},
          context: 'CORNERSTONE',
        },
      ],
    },
  },
  {
    id: 'Cine',
    uiType: TOOL_TYPES.radioGroup,
    props: {
      type: 'toggle',
      icon: 'tool-cine',
      evaluate: evaluate.cine,
      label: 'Cine',
      protocol: [...defaultProtocols],
      commands: [
        {
          commandName: 'toggleCine',
          commandOptions: {},
          context: 'CORNERSTONE',
        },
      ],
    },
  },
  // Measurement
  {
    id: 'MeasurementTools',
    uiType: TOOL_TYPES.splitButton,
    props: {
      groupId: 'MeasurementTools',
      isRadio: true, // ?
      protocol: [...defaultProtocols, 'ecg', 'mpr'],
      evaluate:
        'evaluate.group.promoteToPrimaryIfCornerstoneToolNotActiveInTheList',
      primary: ToolbarService.createButton({
        id: 'ArrowAnnotate',
        icon: 'tool-annotate',
        label: 'Arrow Annotate',
        tooltip: 'Arrow Annotate',
        commands: _createSetToolActiveCommands('ArrowAnnotate'),
        evaluate: evaluate.cornerstoneTool,
      }),
      secondary: {
        tooltip: 'More Measure Tools',
      },
      items: [
        ToolbarService.createButton({
          id: 'Length',
          icon: 'tool-length',
          label: 'Length',
          tooltip: 'Length Tool',
          commands: _createSetToolActiveCommands('Length'),
          protocol: [...defaultProtocols, 'mpr'],
          evaluate: evaluate.cornerstoneTool,
        }),
        ToolbarService.createButton({
          id: 'Bidirectional',
          icon: 'tool-bidirectional',
          label: 'Bidirectional',
          tooltip: 'Bidirectional Tool',
          commands: _createSetToolActiveCommands('Bidirectional'),
          protocol: [...defaultProtocols, 'mpr'],
          evaluate: evaluate.cornerstoneTool,
        }),
        ToolbarService.createButton({
          id: 'ArrowAnnotate',
          icon: 'tool-annotate',
          label: 'Arrow Annotate',
          tooltip: 'Arrow Annotate',
          commands: _createSetToolActiveCommands('ArrowAnnotate'),
          evaluate: evaluate.cornerstoneTool,
        }),
        ToolbarService.createButton({
          id: 'Angle',
          icon: 'tool-angle',
          label: 'Angle',
          tooltip: 'Angle',
          commands: _createSetToolActiveCommands('Angle'),
          protocol: [...defaultProtocols, 'mpr'],
          evaluate: evaluate.cornerstoneTool,
        }),
        ToolbarService.createButton({
          id: 'CobbAngle',
          icon: 'tool-cobb-angle',
          label: 'Cobb Angle',
          tooltip: 'Cobb Angle',
          commands: _createSetToolActiveCommands('CobbAngle'),
          protocol: [...defaultProtocols, 'mpr'],
          evaluate: evaluate.cornerstoneTool,
        }),
        ToolbarService.createButton({
          id: 'PlanarFreehandROI',
          icon: 'tool-polygon',
          label: 'Planar Freehand ROI',
          tooltip: 'Planar Freehand ROI',
          commands: _createSetToolActiveCommands('PlanarFreehandROI'),
          evaluate: evaluate.cornerstoneTool,
        }),
        ToolbarService.createButton({
          id: 'Polyline',
          icon: 'tool-polyline',
          label: 'Polyline',
          tooltip: 'Polyline',
          commands: _createSetToolActiveCommands('Polyline'),
          evaluate: evaluate.cornerstoneTool,
        }),
        // ToolbarService.createButton({
        //   id: 'DragProbe',
        //   icon: 'tool-probe',
        //   label: 'Probe',
        //   tooltip: 'Probe',
        //   commands: _createSetToolActiveCommands('DragProbe'),
        //   evaluate: evaluate.cornerstoneTool,
        // }),
        ToolbarService.createButton({
          id: 'Probe',
          icon: 'tool-probe',
          label: 'Probe',
          tooltip: 'Probe',
          commands: _createSetToolActiveCommands('Probe'),
          protocol: [...defaultProtocols, 'mpr'],
          evaluate: evaluate.cornerstoneTool,
        }),
        ToolbarService.createButton({
          id: 'Rectangle',
          icon: 'tool-rectangle',
          label: 'Rectangle',
          tooltip: 'Rectangle',
          commands: _createSetToolActiveCommands('RectangleROI'),
          protocol: [...defaultProtocols, 'mpr'],
          evaluate: evaluate.cornerstoneTool,
        }),
        ToolbarService.createButton({
          id: 'EllipticalROI',
          icon: 'tool-elipse',
          label: 'Ellipse',
          tooltip: 'Ellipse Tool',
          commands: _createSetToolActiveCommands('EllipticalROI'),
          evaluate: evaluate.cornerstoneTool,
        }),
        ToolbarService.createButton({
          id: 'CircleROI',
          icon: 'tool-circle',
          label: 'Circle',
          tooltip: 'Circle Tool',
          commands: _createSetToolActiveCommands('CircleROI'),
          evaluate: evaluate.cornerstoneTool,
        }),
        // ToolbarService.createButton({
        //   id: 'CalibrationLine',
        //   icon: 'tool-calibration',
        //   label: 'Calibration',
        //   tooltip: 'Calibration Line',
        //   commands: _createSetToolActiveCommands('CalibrationLine'),
        //   protocol: [...defaultProtocols],
        //   evaluate: evaluate.cornerstoneTool,
        // }),
        ToolbarService.createButton({
          id: 'CTRatio',
          icon: 'tool-ct-ratio',
          label: 'CT Ratio',
          tooltip: 'CT Ratio',
          commands: _createSetToolActiveCommands('CTRatio'),
          protocol: [...defaultProtocols, 'mpr'],
          evaluate: evaluate.cornerstoneTool,
        }),
        ToolbarService.createButton({
          id: 'HeartRate',
          icon: 'tool-heart-rate',
          label: 'Heart Rate',
          tooltip: 'Heart Rate',
          protocol: ['ecg'],
          commands: _createSetToolActiveCommands('HeartRate'),
          evaluate: evaluate.cornerstoneTool,
        }),
        ToolbarService.createButton({
          id: 'QTPoint',
          icon: 'tool-qt',
          label: 'QT/QTc',
          tooltip: 'QT/QTc',
          protocol: ['ecg'],
          commands: _createSetToolActiveCommands('QTPoint'),
          evaluate: evaluate.cornerstoneTool,
        }),
        ToolbarService.createButton({
          id: 'Duration',
          icon: 'tool-duration',
          label: 'Duration',
          tooltip: 'Duration',
          protocol: ['ecg'],
          commands: _createSetToolActiveCommands('Duration'),
          evaluate: evaluate.cornerstoneTool,
        }),
        ToolbarService.createButton({
          id: 'Deviation',
          icon: 'tool-deviation',
          label: 'Amplitude Deviation',
          tooltip: 'Amplitude Deviation',
          protocol: ['ecg'],
          commands: _createSetToolActiveCommands('Deviation'),
          evaluate: evaluate.cornerstoneTool,
        }),
        ToolbarService.createButton({
          id: 'QrsAxis',
          icon: 'tool-qrs-axis',
          label: 'QRS Axis',
          tooltip: 'QRS Axis',
          protocol: ['ecg'],
          commands: _createSetToolActiveCommands('QrsAxis'),
          evaluate: evaluate.cornerstoneTool,
        }),
      ],
    },
  },
  // Segmentation
  {
    id: 'SegmentationTools',
    uiType: TOOL_TYPES.splitButton,
    props: {
      groupId: 'SegmentationTools',
      isRadio: true, // ?
      protocol: ['mpr'],
      // Switch?
      primary: _createToolButton(
        'Brush',
        'tool-brush',
        'Brush',
        [
          {
            commandName: 'setToolActive',
            commandOptions: {
              toolName: 'Brush',
            },
            context: 'CORNERSTONE',
          },
        ],
        'Brush'
      ),
      secondary: {
        tooltip: 'More Segmentation Tools',
      },
      items: [
        _createToolButton(
          'Brush',
          'tool-brush',
          'Brush',
          [
            {
              commandName: 'setToolActive',
              commandOptions: {
                toolName: 'Brush',
              },
              context: 'CORNERSTONE',
            },
          ],
          'Brush'
        ),
        _createToolButton(
          'Rectangle Scissors',
          'tool-rectangle-scissor',
          'RectangleScissors',
          [
            {
              commandName: 'setToolActive',
              commandOptions: {
                toolName: 'RectangleScissor',
              },
              context: 'CORNERSTONE',
            },
          ],
          'Rectangle Scissors'
        ),
        _createToolButton(
          'CircleScissors',
          'tool-circle-scissor',
          'Circle Scissors',
          [
            {
              commandName: 'setToolActive',
              commandOptions: {
                toolName: 'CircleScissor',
              },
              context: 'CORNERSTONE',
            },
          ],
          'Circle Scissors'
        ),
        _createToolButton(
          'SphereScissors',
          'tool-sphere-scissor',
          'Sphere Scissors',
          [
            {
              commandName: 'setToolActive',
              commandOptions: {
                toolName: 'SphereScissor',
              },
              context: 'CORNERSTONE',
            },
          ],
          'Sphere Scissors'
        ),
        _createToolButton(
          'PaintFill',
          'tool-paint',
          'Paint Fill',
          [
            {
              commandName: 'setToolActive',
              commandOptions: {
                toolName: 'PaintFill',
              },
              context: 'CORNERSTONE',
            },
          ],
          'Paint Fill'
        ),
        _createToolButton(
          'CircularBrush',
          'tool-circle-brush',
          'Circular Brush',
          [
            {
              commandName: 'setToolActive',
              commandOptions: {
                toolName: 'CircularBrush',
              },
              context: 'CORNERSTONE',
            },
          ],
          'Circular Brush'
        ),
        _createToolButton(
          'CircularEraser',
          'tool-circle-eraser',
          'Circular Eraser',
          [
            {
              commandName: 'setToolActive',
              commandOptions: {
                toolName: 'CircularEraser',
              },
              context: 'CORNERSTONE',
            },
          ],
          'Circular Eraser'
        ),
        _createToolButton(
          'SphereBrush',
          'tool-sphere-brush',
          'Sphere Brush',
          [
            {
              commandName: 'setToolActive',
              commandOptions: {
                toolName: 'SphereBrush',
              },
              context: 'CORNERSTONE',
            },
          ],
          'Sphere Brush'
        ),
        _createToolButton(
          'SphereEraser',
          'tool-sphere-eraser',
          'Sphere Eraser',
          [
            {
              commandName: 'setToolActive',
              commandOptions: {
                toolName: 'SphereEraser',
              },
              context: 'CORNERSTONE',
            },
          ],
          'Sphere Eraser'
        ),
        _createToolButton(
          'ThresholdBrush',
          'tool-threshold-brush',
          'Threshold Brush',
          [
            {
              commandName: 'setToolActive',
              commandOptions: {
                toolName: 'ThresholdBrush',
              },
              context: 'CORNERSTONE',
            },
          ],
          'Threshold Brush'
        ),
      ],
    },
  },
  // Zoom..
  {
    id: 'Zoom',
    uiType: TOOL_TYPES.radioGroup,
    props: {
      type: 'tool',
      icon: 'tool-zoom',
      label: 'Zoom',
      protocol: [...defaultProtocols, 'ecg', 'mpr'],
      commands: setToolActiveToolbar,
      evaluate: evaluate.cornerstoneTool,
    },
  },
  {
    id: 'Invert',
    uiType: TOOL_TYPES.radioGroup,
    props: {
      type: 'tool',
      icon: 'tool-zoom',
      label: 'Invert',
      protocol: [...defaultProtocols],
      commands: [
        {
          commandName: 'invertViewport',
          commandOptions: {},
          context: 'CORNERSTONE',
        },
      ],
    },
  },
  // Window Level + Presets...
  {
    id: 'WindowLevelTools',
    uiType: TOOL_TYPES.splitButton,
    props: {
      groupId: 'WindowLevelTools',
      isRadio: true, // ?
      protocol: [...defaultProtocols, 'mpr'],
      primary: ToolbarService.createButton({
        id: 'WindowLevel',
        icon: 'tool-window-level',
        label: 'Window Level',
        tooltip: 'Window Level',
        commands: _createSetToolActiveCommands('WindowLevel'),
        evaluate: evaluate.cornerstoneTool,
      }),
      secondary: {
        tooltip: 'W/L Presets',
      },
      items: [
        _createWwwcPreset(1, 'Brain', '90', '35'),
        _createWwwcPreset(2, 'Brain Bone', '1600', '450'),
        _createWwwcPreset(3, 'Spine', '4000', '700'),
        _createWwwcPreset(4, 'Lung', '1500', '-400'),
        _createWwwcPreset(5, 'Mediastinum', '350', '40'),
        _createWwwcPreset(6, 'Abdomen', '1500', '-700'),
        _createWwwcPreset(7, 'Liver', '400', '40'),
        _createWwwcPreset(8, 'Bone', '1600', '550'),
        _createWwwcPreset(9, 'Vessel', '500', '40'),
      ],
    },
  },
  // Pan...
  {
    id: 'Pan',
    uiType: TOOL_TYPES.radioGroup,
    // protocol: [...defaultProtocols, 'mpr'],
    props: {
      type: 'tool',
      icon: 'tool-move',
      label: 'Pan',
      protocol: [...defaultProtocols, 'ecg', 'mpr'],
      commands: setToolActiveToolbar,
      evaluate: evaluate.cornerstoneTool,
    },
  },
  {
    id: 'Magnify',
    uiType: TOOL_TYPES.radioGroup,
    props: {
      type: 'tool',
      label: 'Magnify',
      icon: 'tool-magnify',
      protocol: [...defaultProtocols],
      commands: _createSetToolActiveCommands('Magnify'),
      evaluate: evaluate.cornerstoneTool,
    },
  },
  {
    id: 'Download',
    uiType: TOOL_TYPES.action,
    props: {
      icon: 'download',
      label: 'Download',
      type: 'action',
      protocol: [...defaultProtocols, 'ecg', 'mpr'],
      commands: [
        {
          commandName: 'showDownloadViewportModal',
          commandOptions: {},
          context: 'CORNERSTONE',
        },
      ],
    },
  },
  {
    id: 'Delete',
    uiType: TOOL_TYPES.action,
    props: {
      icon: 'tool-delete',
      label: 'Delete DICOM Study',
      type: 'action',
      protocol: [...defaultProtocols, 'ecg'],
      commands: [
        {
          commandName: 'showDeleteViewportModal',
          commandOptions: {},
          context: 'CORNERSTONE',
        },
      ],
    },
  },
  {
    id: 'Layout',
    uiType: 'ohif.layoutSelector',
    props: {
      rows: 6,
      columns: 6,
      protocol: [...defaultProtocols, 'ecg'],
    },
  },
  {
    id: 'MPR',
    uiType: TOOL_TYPES.action,
    props: {
      type: 'toggle',
      icon: 'icon-mpr',
      label: 'MPR',
      protocol: [...defaultProtocols],
      commands: [
        {
          commandName: 'toggleMPR',
          commandOptions: {},
          context: 'CORNERSTONE',
        },
      ],
    },
  },
  {
    id: 'VR',
    uiType: TOOL_TYPES.action,
    props: {
      type: 'toggle',
      icon: 'icon-vr',
      label: 'VR',
      protocol: [...defaultProtocols],
      commands: [
        {
          commandName: 'toggleVR',
          commandOptions: {},
          context: 'CORNERSTONE',
        },
      ],
    },
  },
  {
    id: 'Crosshairs',
    uiType: TOOL_TYPES.radioGroup,
    props: {
      type: 'action',
      icon: 'tool-crosshair',
      label: 'Crosshairs',
      protocol: ['mpr'],
      evaluate: 'evaluate.cornerstoneTool.toggle.ifStrictlyDisabled',
      commands: [
        {
          commandName: 'toggleCrosshairs',
          commandOptions: {
            toolGroupId: 'mpr',
            toolName: 'Crosshairs',
          },
          context: 'CORNERSTONE',
        },
      ],
    },
  },
  {
    id: 'CrosshairsOrthogonal',
    uiType: TOOL_TYPES.radioGroup,
    props: {
      type: 'action',
      icon: 'tool-crosshair',
      label: 'Crosshairs',
      protocol: ['orthogonal'],
      commands: [
        {
          commandName: 'toggleCrosshairs',
          commandOptions: {
            toolGroupId: 'orthogonal',
            toolName: 'Crosshairs',
          },
          context: 'CORNERSTONE',
        },
      ],
    },
  },
  {
    id: 'VR3DRotate',
    uiType: TOOL_TYPES.radioGroup,
    props: {
      type: 'tool',
      icon: 'tool-vr-rotate',
      evaluate: evaluate.vtkjs,
      label: '3D Rotate',
      protocol: ['vr'],
      commands: [
        {
          commandName: 'setVRToolActive',
          commandOptions: {
            toolName: 'VR3DRotate',
          },
          context: 'CORNERSTONE',
        },
      ],
    },
  },
  {
    id: 'VR3DPan',
    uiType: TOOL_TYPES.radioGroup,
    props: {
      type: 'tool',
      icon: 'tool-move',
      evaluate: evaluate.vtkjs,
      label: '3D Pan',
      protocol: ['vr'],
      commands: [
        {
          commandName: 'setVRToolActive',
          commandOptions: {
            toolName: 'VR3DPan',
          },
          context: 'CORNERSTONE',
        },
      ],
    },
  },
  {
    id: 'VR3DZoom',
    uiType: TOOL_TYPES.radioGroup,
    props: {
      type: 'tool',
      icon: 'tool-zoom',
      evaluate: evaluate.vtkjs,
      label: '3D Zoom',
      protocol: ['vr'],
      commands: [
        {
          commandName: 'setVRToolActive',
          commandOptions: {
            toolName: 'VR3DZoom',
          },
          context: 'CORNERSTONE',
        },
      ],
    },
  },
  {
    id: 'VRAdjustWindow',
    uiType: TOOL_TYPES.radioGroup,
    props: {
      type: 'tool',
      icon: 'tool-window-level',
      evaluate: evaluate.vtkjs,
      label: 'Adjust Window',
      protocol: ['vr'],
      commands: [
        {
          commandName: 'setVRToolActive',
          commandOptions: {
            toolName: 'VRAdjustWindow',
          },
          context: 'CORNERSTONE',
        },
      ],
    },
  },
  {
    id: 'VRLength',
    uiType: TOOL_TYPES.radioGroup,
    props: {
      type: 'tool',
      icon: 'tool-vr-distance',
      evaluate: evaluate.vtkjs,
      label: 'Length',
      protocol: ['vr'],
      commands: [
        {
          commandName: 'setVRToolActive',
          commandOptions: {
            toolName: 'VRLength',
          },
          context: 'CORNERSTONE',
        },
      ],
    },
  },
  {
    id: 'VRRectangleScalpel',
    uiType: TOOL_TYPES.radioGroup,
    props: {
      type: 'tool',
      icon: 'tool-vr-rect',
      evaluate: evaluate.vtkjs,
      label: 'Rectangle Scalpel',
      protocol: ['vr'],
      commands: [
        {
          commandName: 'setVRToolActive',
          commandOptions: {
            toolName: 'VRRectangleScalpel',
          },
          context: 'CORNERSTONE',
        },
      ],
    },
  },
  {
    id: 'VRFreehandScalpel',
    uiType: TOOL_TYPES.radioGroup,
    props: {
      type: 'tool',
      icon: 'tool-vr-freehand',
      evaluate: evaluate.vtkjs,
      label: 'Freehand Scalpel',
      protocol: ['vr'],
      commands: [
        {
          commandName: 'setVRToolActive',
          commandOptions: {
            toolName: 'VRFreehandScalpel',
          },
          context: 'CORNERSTONE',
        },
      ],
    },
  },
  {
    id: 'VRRemoveBed',
    uiType: TOOL_TYPES.radioGroup,
    props: {
      type: 'action',
      icon: 'tool-vr-remove-bed',
      label: 'Remove Bed',
      protocol: ['vr'],
      commands: [
        {
          commandName: 'setVRToolActive',
          commandOptions: {
            toolName: 'VRRemoveBed',
          },
          context: 'CORNERSTONE',
        },
      ],
    },
  },
  {
    id: 'VRReset',
    uiType: TOOL_TYPES.radioGroup,
    props: {
      type: 'action',
      icon: 'tool-reset',
      label: 'Reset view',
      protocol: ['vr'],
      commands: [
        {
          commandName: 'setVRToolActive',
          commandOptions: {
            toolName: 'VRReset',
          },
          context: 'CORNERSTONE',
        },
      ],
    },
  },
  {
    id: 'VRSettings',
    uiType: 'vindr.settings',
    props: {
      icon: 'tool-vr-setting',
      tooltip: 'Settings',
      label: 'Settings',
      protocol: ['vr'],
    },
  },
  {
    id: 'ECGSettings',
    uiType: 'ecg.settings',
    props: {
      icon: 'tool-vr-setting',
      tooltip: 'Settings',
      label: 'Settings',
      protocol: ['ecg'],
    },
  },
  {
    id: 'CaptureKeyImage',
    uiType: TOOL_TYPES.action,
    props: {
      icon: 'tool-capture',
      label: 'Capture Key Image',
      type: 'action',
      protocol: [...defaultProtocols, 'ecg', 'mpr'],
      commands: [
        {
          commandName: 'captureKeyImage',
          commandOptions: {},
        },
      ],
    },
  },
  {
    id: 'Print',
    uiType: TOOL_TYPES.action,
    props: {
      icon: 'tool-print',
      label: 'Print',
      type: 'action',
      evaluate: evaluate.sidePanel,
      protocol: [...defaultProtocols, 'ecg'],
      commands: [
        {
          commandName: 'togglePrint',
          commandOptions: {},
        },
      ],
    },
  },
  {
    id: 'TagBrowser',
    uiType: 'vindr.checkboxGroup',
    props: {
      groupId: 'TagBrowser',
      protocol: [...defaultProtocols, 'ecg', 'mpr', 'vr'],
      label: 'Info',
      tooltip: 'Info',
      icon: 'list-bullets',
      items: [
        ToolbarService.createButton({
          id: 'PatientInfo',
          label: 'Patient Information',
          tooltip: 'Patient Information',
          commands: [
            {
              commandName: 'showPatientInfo',
              context: 'CORNERSTONE',
            },
          ],
          evaluate: 'evaluate.patientInfo',
        }),
        ToolbarService.createButton({
          id: 'TagBrowser',
          icon: 'list-bullets',
          label: 'Dicom Tag Browser',
          tooltip: 'Dicom Tag Browser',
          commands: [
            {
              commandName: 'openDICOMTagViewer',
              commandOptions: {},
              context: 'DEFAULT',
            },
          ],
        }),
      ],
    },
  },
  {
    id: 'CAD',
    uiType: TOOL_TYPES.action,
    props: {
      type: 'toggle',
      icon: 'icon-cad',
      label: 'CAD',
      evaluate: evaluate.sidePanel,
      protocol: [...defaultProtocols, 'ecg', 'mpr', 'vr'],
      commands: [
        {
          commandName: 'toggleCAD',
          commandOptions: {},
          context: 'DEFAULT',
        },
      ],
    },
  },
  {
    id: 'PresentationState',
    uiType: TOOL_TYPES.action,
    props: {
      type: 'toggle',
      icon: 'book',
      label: 'Recorded Annotation',
      evaluate: evaluate.sidePanel,
      protocol: [...defaultProtocols, 'ecg'],
      commands: [
        {
          commandName: 'togglePresentationState',
          commandOptions: {},
          context: 'DEFAULT',
        },
      ],
    },
  },
  {
    id: 'Consultation',
    uiType: TOOL_TYPES.action,
    props: {
      type: 'toggle',
      icon: 'tool-consultation',
      label: 'Consultation',
      evaluate: evaluate.sidePanel,
      protocol: [...defaultProtocols, 'ecg', 'mpr', 'vr'],
      commands: [
        {
          commandName: 'toggleConsultation',
          commandOptions: {},
          context: 'DEFAULT',
        },
      ],
    },
  },
];

export default toolbarButtons;
